import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import logger from '@/utils/logger';

type VideoMap = Partial<Record<'greet' | 'default' | 'talk' | 'think' | 'listen', string>>;

export interface SceneThemeState {
  sceneId: string;
  logoSrc: string | null;
  hasLogo: boolean;
  bgUrl: string | null;
  heroCSS: string | null;
  videos: VideoMap | null;
  processed: boolean;
}

interface SceneThemeContextType {
  state: SceneThemeState;
  refresh: () => void;
}

const SceneThemeContext = createContext<SceneThemeContextType | null>(null);

interface SceneThemeProviderProps {
  children: ReactNode;
}

/**
 * SceneTheme Context Provider - 统一管理场景主题状态
 * 解决多个组件重复调用useSceneTheme导致的性能问题
 */
export function SceneThemeProvider({ children }: SceneThemeProviderProps) {
  const location = useLocation();

  const [state, setState] = useState<SceneThemeState>({
    sceneId: 'Custom',
    logoSrc: null,
    hasLogo: false,
    bgUrl: null,
    heroCSS: null,
    videos: null,
    processed: false,
  });

  const getSceneIdFromPath = (pathname: string): string => {
    const match = pathname.match(/\/scene\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]) : 'Custom';
  };

  const normalizePath = (src: string): string => {
    const s = typeof src === 'string' ? src.trim() : '';
    if (!s) return s;
    if (s.startsWith('http') || s.startsWith('/')) return s;
    return `/${s}`;
  };

  const safeParseJSON = <T = unknown,>(raw: string | null): T | null => {
    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch (e) {
      logger.warn('[SceneThemeContext] JSON 解析失败:', e);
      return null;
    }
  };

  const refresh = useCallback(() => {
    const sceneId = getSceneIdFromPath(location.pathname);

    // 读取配置（优先命名空间，其次兼容旧键）
    const nsConfigRaw = sessionStorage.getItem(`theme:${sceneId}:config`);
    const legacyConfigRaw = sessionStorage.getItem('themeConfig');
    const themeConfig = safeParseJSON<any>(nsConfigRaw ?? legacyConfigRaw);

    // 读取其他命名空间键
    const bgUrlRaw =
      sessionStorage.getItem(`theme:${sceneId}:bg`) ||
      sessionStorage.getItem('themeBackgroundImage');
    const heroCSSRaw =
      sessionStorage.getItem(`theme:${sceneId}:hero`) || sessionStorage.getItem('themeHeroCSS');
    const videosRaw =
      sessionStorage.getItem(`theme:${sceneId}:videos`) ||
      sessionStorage.getItem('themeAvatarVideos');
    const rawVideosObj = safeParseJSON<VideoMap>(videosRaw);
    const videosObj =
      rawVideosObj && typeof rawVideosObj === 'object'
        ? (Object.fromEntries(
            Object.entries(rawVideosObj)
              .filter(([_, v]) => typeof v === 'string' && (v as string).trim().length > 0)
              .map(([k, v]) => [k, normalizePath(v as string)])
          ) as VideoMap)
        : null;

    const processed = Boolean(themeConfig?.sceneProcessed) || Boolean(rawVideosObj);

    // 处理 logo
    const logoImage = themeConfig?.logoImage;
    const hasLogo = typeof logoImage === 'string' && logoImage.trim().length > 0;
    const logoSrc = hasLogo ? normalizePath(logoImage) : null;

    const newState = {
      sceneId,
      logoSrc,
      hasLogo,
      bgUrl: bgUrlRaw || null,
      heroCSS: heroCSSRaw || null,
      videos: videosObj,
      processed,
    };

    // 使用深度比较避免不必要的更新
    setState((prevState) => {
      const stateChanged = JSON.stringify(prevState) !== JSON.stringify(newState);
      if (stateChanged) {
        logger.debug('[SceneThemeContext] 刷新主题状态:', {
          sceneId,
          hasLogo,
          logoSrc,
          bgUrl: Boolean(bgUrlRaw),
          heroCSS: Boolean(heroCSSRaw),
          videos: videosObj ? Object.keys(videosObj) : null,
          processed,
        });
        return newState;
      }
      return prevState;
    });
  }, [location.pathname]);

  // 路由变化时刷新
  useEffect(() => {
    refresh();
  }, [refresh]);

  // 监听主题更新事件
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener('theme-config-updated', handler as EventListener);
    window.addEventListener('storage', handler as EventListener);
    return () => {
      window.removeEventListener('theme-config-updated', handler as EventListener);
      window.removeEventListener('storage', handler as EventListener);
    };
  }, [refresh]);

  // 应用heroCSS到CSS变量
  useEffect(() => {
    if (state.heroCSS && typeof state.heroCSS === 'string') {
      document.documentElement.style.setProperty('--theme-hero', state.heroCSS);
      logger.debug('[SceneThemeContext] 应用 heroCSS 至 --theme-hero');
    } else {
      document.documentElement.style.removeProperty('--theme-hero');
      logger.debug('[SceneThemeContext] 清除 --theme-hero');
    }
  }, [state.heroCSS]);

  const contextValue = useMemo(() => ({ state, refresh }), [state, refresh]);

  return <SceneThemeContext.Provider value={contextValue}>{children}</SceneThemeContext.Provider>;
}

/**
 * 使用场景主题的Hook - 优化版本
 */
export function useSceneTheme(): SceneThemeState {
  const context = useContext(SceneThemeContext);
  if (!context) {
    throw new Error('useSceneTheme must be used within a SceneThemeProvider');
  }
  return context.state;
}

/**
 * 使用场景Logo的Hook - 优化版本
 */
export function useSceneLogo() {
  const { logoSrc, hasLogo } = useSceneTheme();
  return { logoSrc, shouldShowLogo: hasLogo };
}
